
import React from 'react';
import PropTypes from 'prop-types';
import { toPathString } from '@pinto/path-utils';
import type { FieldInput, FieldMeta } from '@pinto/react-form';
import memoizeOne from 'memoize-one';

import JSONRenderer, { DefaultRowRenderer, JSONRendererProps, JSONRowRenderer } from './JSONRenderer';

export type JSONCoverageProps = JSONRendererProps & { coverage: Coverage };

export interface JSONCoverageFieldProps extends JSONCoverageProps {
    input: FieldInput;
    meta?: FieldMeta;
}

type Coverage = {
    covered?: string[];
    warn?: string[];
    notCovered?: string[];
};

type CoverageMap = { [key: string]: keyof JSONCoverageProps['coverage'] };

const CoverageContext = React.createContext<{ coverage: Coverage, coverageMap: CoverageMap }>({
    coverage: {},
    coverageMap: {},
});

export class JSONCoverage extends React.Component<JSONCoverageProps> {
    props!: JSONCoverageProps;

    static propTypes = {
        data: PropTypes.any,
        coverage: PropTypes.shape({
            covered: PropTypes.arrayOf(PropTypes.string),
            warn: PropTypes.arrayOf(PropTypes.string),
            notCovered: PropTypes.arrayOf(PropTypes.string),
        }).isRequired,
    };

    getContextValue = memoizeOne((coverage: Coverage) => {
        const coverageMap: CoverageMap = {};

        for (const type in coverage) {
            for (const path of coverage[type as keyof typeof coverage]!) {
                coverageMap[path] = type as keyof typeof coverage;
            }
        }

        return {
            coverage,
            coverageMap,
        };
    });

    render() {
        const { coverage, ...props } = this.props;

        return <CoverageContext.Provider value={this.getContextValue(coverage)}>
            <Root>
                <JSONRenderer {...props} RowRenderer={CoverageRowRenderer} />
            </Root>
        </CoverageContext.Provider>
    }
}

export default JSONCoverage;

export const JSONCoverageField: React.FC<JSONCoverageFieldProps> = ({ input, meta, ...rest }) =>
    <JSONCoverage {...rest} data={input.value} />;

export class CoverageRowRenderer extends React.Component<React.ComponentProps<JSONRowRenderer>> {
    context!: React.ContextType<typeof CoverageContext>;
    static contextType = CoverageContext;

    render() {
        const { data: { path } } = this.props;
        const { coverage, coverageMap } = this.context;
        const { covered = [], warn = [], notCovered = [] } = coverage;
        let coverageType: any;

        if (path.length === 0) {
            // coverageType = 'warn';
            // if (warn.length === 0 && notCovered.length === 0) coverageType = 'covered';
            // if (covered.length < 1) coverageType = 'notCovered';
        } else {
            // Infer the coverageType either by finding an exact match in the coverageMap
            // Or by inheriting the coverage information from any parent that has one
            for (let i = path.length; i > 0; --i) {
                const pathStr = toPathString(path.slice(0, i));
                if (coverageMap[pathStr]) {
                    coverageType = coverageMap[pathStr];
                    break;
                }
            }
        }

        return <DefaultRowRenderer
            {...this.props}
            data-coverage={coverageType}
        />;
    }
}


require('!style-loader/lib/addStyles.js')([[ module.id, ".flex {\n  display: flex;\n}\n.flex.flex-space-between {\n  align-items: space-between;\n}\n.center {\n  text-align: center;\n}\n.bold {\n  font-weight: bold;\n}\n.mt0 {\n  margin-top: 0rem !important;\n}\n.mb0 {\n  margin-bottom: 0rem !important;\n}\n.ml0 {\n  margin-left: 0rem !important;\n}\n.mr0 {\n  margin-right: 0rem !important;\n}\n.m0 {\n  margin: 0rem 0rem 0rem 0rem !important;\n}\n.mx0 {\n  margin-left: 0rem !important;\n  margin-right: 0rem !important;\n}\n.my0 {\n  margin-top: 0rem !important;\n  margin-bottom: 0rem !important;\n}\n.mt1 {\n  margin-top: 1rem !important;\n}\n.mb1 {\n  margin-bottom: 1rem !important;\n}\n.ml1 {\n  margin-left: 1rem !important;\n}\n.mr1 {\n  margin-right: 1rem !important;\n}\n.m1 {\n  margin: 1rem 1rem 1rem 1rem !important;\n}\n.mx1 {\n  margin-left: 1rem !important;\n  margin-right: 1rem !important;\n}\n.my1 {\n  margin-top: 1rem !important;\n  margin-bottom: 1rem !important;\n}\n.mt2 {\n  margin-top: 2rem !important;\n}\n.mb2 {\n  margin-bottom: 2rem !important;\n}\n.ml2 {\n  margin-left: 2rem !important;\n}\n.mr2 {\n  margin-right: 2rem !important;\n}\n.m2 {\n  margin: 2rem 2rem 2rem 2rem !important;\n}\n.mx2 {\n  margin-left: 2rem !important;\n  margin-right: 2rem !important;\n}\n.my2 {\n  margin-top: 2rem !important;\n  margin-bottom: 2rem !important;\n}\n.mt05 {\n  margin-top: 0.5rem !important;\n}\n.mb05 {\n  margin-bottom: 0.5rem !important;\n}\n.ml05 {\n  margin-left: 0.5rem !important;\n}\n.mr05 {\n  margin-right: 0.5rem !important;\n}\n.m05 {\n  margin: 0.5rem 0.5rem 0.5rem 0.5rem !important;\n}\n.mx05 {\n  margin-left: 0.5rem !important;\n  margin-right: 0.5rem !important;\n}\n.my05 {\n  margin-top: 0.5rem !important;\n  margin-bottom: 0.5rem !important;\n}\n.mt025 {\n  margin-top: 0.25rem !important;\n}\n.mb025 {\n  margin-bottom: 0.25rem !important;\n}\n.ml025 {\n  margin-left: 0.25rem !important;\n}\n.mr025 {\n  margin-right: 0.25rem !important;\n}\n.m025 {\n  margin: 0.25rem 0.25rem 0.25rem 0.25rem !important;\n}\n.mx025 {\n  margin-left: 0.25rem !important;\n  margin-right: 0.25rem !important;\n}\n.my025 {\n  margin-top: 0.25rem !important;\n  margin-bottom: 0.25rem !important;\n}\n.pt0 {\n  padding-top: 0rem !important;\n}\n.pb0 {\n  padding-bottom: 0rem !important;\n}\n.pl0 {\n  padding-left: 0rem !important;\n}\n.pr0 {\n  padding-right: 0rem !important;\n}\n.p0 {\n  padding: 0rem 0rem 0rem 0rem !important;\n}\n.px0 {\n  padding-left: 0rem !important;\n  padding-right: 0rem !important;\n}\n.py0 {\n  padding-top: 0rem !important;\n  padding-bottom: 0rem !important;\n}\n.pt1 {\n  padding-top: 1rem !important;\n}\n.pb1 {\n  padding-bottom: 1rem !important;\n}\n.pl1 {\n  padding-left: 1rem !important;\n}\n.pr1 {\n  padding-right: 1rem !important;\n}\n.p1 {\n  padding: 1rem 1rem 1rem 1rem !important;\n}\n.px1 {\n  padding-left: 1rem !important;\n  padding-right: 1rem !important;\n}\n.py1 {\n  padding-top: 1rem !important;\n  padding-bottom: 1rem !important;\n}\n.pt2 {\n  padding-top: 2rem !important;\n}\n.pb2 {\n  padding-bottom: 2rem !important;\n}\n.pl2 {\n  padding-left: 2rem !important;\n}\n.pr2 {\n  padding-right: 2rem !important;\n}\n.p2 {\n  padding: 2rem 2rem 2rem 2rem !important;\n}\n.px2 {\n  padding-left: 2rem !important;\n  padding-right: 2rem !important;\n}\n.py2 {\n  padding-top: 2rem !important;\n  padding-bottom: 2rem !important;\n}\n.pt05 {\n  padding-top: 0.5rem !important;\n}\n.pb05 {\n  padding-bottom: 0.5rem !important;\n}\n.pl05 {\n  padding-left: 0.5rem !important;\n}\n.pr05 {\n  padding-right: 0.5rem !important;\n}\n.p05 {\n  padding: 0.5rem 0.5rem 0.5rem 0.5rem !important;\n}\n.px05 {\n  padding-left: 0.5rem !important;\n  padding-right: 0.5rem !important;\n}\n.py05 {\n  padding-top: 0.5rem !important;\n  padding-bottom: 0.5rem !important;\n}\n.pt025 {\n  padding-top: 0.25rem !important;\n}\n.pb025 {\n  padding-bottom: 0.25rem !important;\n}\n.pl025 {\n  padding-left: 0.25rem !important;\n}\n.pr025 {\n  padding-right: 0.25rem !important;\n}\n.p025 {\n  padding: 0.25rem 0.25rem 0.25rem 0.25rem !important;\n}\n.px025 {\n  padding-left: 0.25rem !important;\n  padding-right: 0.25rem !important;\n}\n.py025 {\n  padding-top: 0.25rem !important;\n  padding-bottom: 0.25rem !important;\n}\n.font-small-1 {\n  font-size: 0.857142857142857rem;\n}\n.font-large-1 {\n  font-size: 1.142857142857143rem;\n}\n.font-small-2 {\n  font-size: 0.714285714285714rem;\n}\n.font-large-2 {\n  font-size: 1.285714285714286rem;\n}\n.font-small-3 {\n  font-size: 0.571428571428571rem;\n}\n.font-large-3 {\n  font-size: 1.428571428571429rem;\n}\n.font-small-4 {\n  font-size: 0.428571428571429rem;\n}\n.font-large-4 {\n  font-size: 1.571428571428571rem;\n}\n.JSONCoverage-Root--owRcu [data-coverage='covered'] {\n  background-color: #e0f7e1;\n}\n.JSONCoverage-Root--owRcu [data-coverage='warn'] {\n  background-color: #fff4d9;\n}\n.JSONCoverage-Root--owRcu [data-coverage='notCovered'] {\n  background-color: #ffe2db;\n}\n.bp3-dark .JSONCoverage-Root--owRcu [data-coverage='covered'] {\n  background-color: #2c4a44;\n}\n.bp3-dark .JSONCoverage-Root--owRcu [data-coverage='warn'] {\n  background-color: #4c4f3f;\n}\n.bp3-dark .JSONCoverage-Root--owRcu [data-coverage='notCovered'] {\n  background-color: #523e48;\n}\n", '' ]]);
const { RSL_COMPONENT } = require("!../../node_modules/@pinto/react-style-loader/react-style-loader.runtime.compiled.js");
const __Root = RSL_COMPONENT('Root', 'JSONCoverageRoot', 'JSONCoverage-Root--owRcu');
 const Root = __Root

